import { z } from 'zod';
import { CursorPageQuery, IsoDate, IsoDateTime, QueryBoolean, Uuid, Version } from './common.js';
import { ModuleCode, PlanCode, SubscriptionStatus } from './modules.js';
/**
 * Horevia Pay — abonnements (ADR-023, phase H8) : facturation des abonnements
 * **par la plateforme** à ses tenants. Ce n'est pas un module du catalogue :
 * la console émet, encaisse et relance ; le tenant voit ses factures.
 *
 * Aucun prix, délai ni taux ici : tout vient de la base (catalogue de tarifs,
 * abonnement, paramètres du tenant `platform`) ou des variables de la
 * plateforme (R-PAY-07).
 */
/** `MANUAL` : facturé hors plateforme ou gratuit, jamais coupé ; `INVOICED` : facturé par HOREVIA. */
export const SubscriptionBillingMode = z.enum(['MANUAL', 'INVOICED']);
export const BillingPeriod = z.enum(['MONTHLY', 'YEARLY']);
/** Limites de plan (R-PAY-05) : `null` = illimité. */
export const PlanLimits = z.object({
    properties: z.number().int().positive().nullable(),
    resources: z.number().int().positive().nullable(),
    users: z.number().int().positive().nullable(),
});
export const PlanLimitKind = z.enum(['properties', 'resources', 'users']);
export const PlanLimitUsage = z.object({
    used: z.number().int().nonnegative(),
    max: z.number().int().positive().nullable(),
});
export const PlanLimitsUsage = z.object({
    properties: PlanLimitUsage,
    resources: PlanLimitUsage,
    users: PlanLimitUsage,
});
/** Prix catalogué d'un plan ou d'une option, par devise et périodicité. */
export const CatalogPrice = z.object({
    currencyCode: z.string().length(3).toUpperCase(),
    billingPeriod: BillingPeriod,
    amount: z.number().nonnegative(),
});
export const PlanPricing = z.object({
    planCode: PlanCode,
    planName: z.string(),
    isActive: z.boolean(),
    limits: PlanLimits,
    prices: z.array(CatalogPrice),
});
export const ModulePricing = z.object({
    moduleCode: ModuleCode,
    moduleName: z.string(),
    prices: z.array(CatalogPrice),
});
/** Catalogue des tarifs et limites (`GET /platform/pricing`). */
export const PricingCatalog = z.object({
    plans: z.array(PlanPricing),
    modules: z.array(ModulePricing),
});
/** Remplacement complet du catalogue (`PUT /platform/pricing`). */
export const UpdatePricingRequest = z.object({
    plans: z.array(z.object({
        planCode: PlanCode,
        limits: PlanLimits,
        prices: z.array(CatalogPrice).max(50),
    })),
    modules: z.array(z.object({
        moduleCode: ModuleCode,
        prices: z.array(CatalogPrice).max(50),
    })),
});
export const SubscriptionInvoiceStatus = z.enum(['ISSUED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED']);
export const SubscriptionPaymentMethod = z.enum([
    'BANK_TRANSFER',
    'MOBILE_MONEY',
    'CASH',
    'CHEQUE',
    'OTHER',
]);
export const SubscriptionPayment = z.object({
    id: Uuid,
    invoiceId: Uuid,
    amount: z.number(),
    currencyCode: z.string().length(3),
    method: SubscriptionPaymentMethod,
    /** Opérateur ou banque (« Orange Money », « Coris Bank »), texte libre. */
    provider: z.string().nullable(),
    reference: z.string().nullable(),
    paidAt: IsoDateTime,
    notes: z.string().nullable(),
    recordedByName: z.string().nullable(),
    reversedAt: IsoDateTime.nullable(),
    reversalReason: z.string().nullable(),
    createdAt: IsoDateTime,
});
/** Facture d'abonnement émise par la plateforme (R-PAY-01). */
export const SubscriptionInvoice = z.object({
    id: Uuid,
    tenantId: Uuid,
    tenantSlug: z.string(),
    tenantName: z.string(),
    subscriptionId: Uuid,
    number: z.string(),
    status: SubscriptionInvoiceStatus,
    /** Émise ou partiellement réglée et échéance dépassée. */
    overdue: z.boolean(),
    issueDate: IsoDate,
    dueDate: IsoDate,
    periodStart: IsoDate,
    periodEnd: IsoDate,
    planCode: PlanCode,
    planName: z.string(),
    billingPeriod: BillingPeriod,
    currencyCode: z.string().length(3),
    subtotal: z.number(),
    taxTotal: z.number(),
    total: z.number(),
    paidTotal: z.number(),
    balance: z.number(),
    cancelledAt: IsoDateTime.nullable(),
    cancelReason: z.string().nullable(),
    payments: z.array(SubscriptionPayment),
    createdAt: IsoDateTime,
    version: Version,
});
export const ListSubscriptionInvoicesQuery = CursorPageQuery.extend({
    status: SubscriptionInvoiceStatus.optional(),
    tenantId: Uuid.optional(),
    /** Seulement les factures échues non réglées. */
    overdue: QueryBoolean.optional(),
    /** Bornes sur la date d'émission (incluses). */
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const RecordSubscriptionPaymentRequest = z.object({
    amount: z.number().positive('Le montant doit être strictement positif'),
    method: SubscriptionPaymentMethod,
    provider: z.string().trim().max(80).nullable().optional(),
    reference: z.string().trim().max(120).nullable().optional(),
    /** Défaut : maintenant. */
    paidAt: IsoDateTime.optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
});
export const CancelSubscriptionInvoiceRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const ReverseSubscriptionPaymentRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** Montants agrégés par devise (la plateforme facture dans la devise de chaque tenant). */
export const MoneyByCurrency = z.object({
    currencyCode: z.string().length(3),
    amount: z.number(),
    count: z.number().int().nonnegative(),
});
/** Tableau financier de la console (`GET /platform/billing/overview`). */
export const PlatformBillingOverview = z.object({
    /** Revenu mensuel récurrent des abonnements facturés vivants (annuel ÷ 12), par devise. */
    mrr: z.array(MoneyByCurrency),
    /** Émis non réglé (échu ou non). */
    outstanding: z.array(MoneyByCurrency),
    /** Échu non réglé. */
    overdue: z.array(MoneyByCurrency),
    /** Réglé sur les trente derniers jours. */
    paidLast30Days: z.array(MoneyByCurrency),
    /** Tenants dont une facture est échue, ou dont une suspension est programmée. */
    overdueTenants: z.array(z.object({
        tenantId: Uuid,
        tenantSlug: z.string(),
        tenantName: z.string(),
        subscriptionStatus: SubscriptionStatus,
        currencyCode: z.string().length(3),
        balance: z.number(),
        oldestDueDate: IsoDate,
        suspensionScheduledAt: IsoDate.nullable(),
    })),
    /** Prochaines factures à émettre sous trente jours (renouvellements et fins d'essai). */
    upcoming: z.array(z.object({
        tenantId: Uuid,
        tenantSlug: z.string(),
        tenantName: z.string(),
        planName: z.string(),
        currencyCode: z.string().length(3),
        amount: z.number(),
        invoiceOn: IsoDate,
        kind: z.enum(['RENEWAL', 'TRIAL_END']),
    })),
});
/** Rapport d'un passage du job de facturation (`POST /platform/billing/run`). */
export const SubscriptionBillingRunReport = z.object({
    asOf: IsoDate,
    scanned: z.number().int().nonnegative(),
    activated: z.number().int().nonnegative(),
    invoiced: z.number().int().nonnegative(),
    pastDue: z.number().int().nonnegative(),
    notices: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
/** Abonnement vu par le tenant (Administration › Paramètres › Abonnement). */
export const TenantSubscription = z.object({
    planCode: PlanCode,
    planName: z.string(),
    status: SubscriptionStatus,
    billingMode: SubscriptionBillingMode,
    billingPeriod: BillingPeriod.nullable(),
    priceAmount: z.number().nullable(),
    currencyCode: z.string().length(3).nullable(),
    startsAt: IsoDateTime,
    trialEndsAt: IsoDateTime.nullable(),
    endsAt: IsoDateTime.nullable(),
    currentPeriodStart: IsoDate.nullable(),
    currentPeriodEnd: IsoDate.nullable(),
    nextInvoiceAt: IsoDate.nullable(),
    pastDueSince: IsoDate.nullable(),
    suspensionScheduledAt: IsoDate.nullable(),
});
/** Comment régler HOREVIA : identité et coordonnées de l'émetteur (fiche du tenant `platform`). */
export const PaymentInstructions = z.object({
    issuerName: z.string(),
    bankName: z.string().nullable(),
    accountNumber: z.string().nullable(),
    iban: z.string().nullable(),
    swift: z.string().nullable(),
    paymentMethodsText: z.string(),
    paymentTermsText: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
});
export const TenantSubscriptionView = z.object({
    subscription: TenantSubscription.nullable(),
    limits: PlanLimitsUsage,
    invoices: z.array(SubscriptionInvoice),
    paymentInstructions: PaymentInstructions,
});
/** Bandeau d'abonnement (`CurrentUser.subscriptionNotice`, porteurs de `module.read`). */
export const SubscriptionNotice = z.object({
    kind: z.enum(['TRIAL_ENDING', 'PAST_DUE', 'SUSPENSION_SCHEDULED', 'ENDING']),
    /** Date pivot : fin d'essai, suspension programmée, fin d'abonnement ou échéance dépassée. */
    date: IsoDate,
    invoiceNumber: z.string().nullable(),
    balance: z.number().nullable(),
    currencyCode: z.string().length(3).nullable(),
});
/** Champs de facturation d'un abonnement (console) — communs à la création et à la mise à jour. */
export const SubscriptionBillingFields = z.object({
    billingMode: SubscriptionBillingMode.optional(),
    billingPeriod: BillingPeriod.nullable().optional(),
    /** Prix négocié par période, dans la devise du tenant (défaut proposé par le catalogue). */
    priceAmount: z.number().nonnegative().nullable().optional(),
    autoSuspend: z.boolean().optional(),
    /** Surcharge des limites du plan ; `null` = limites du plan. */
    limits: PlanLimits.nullable().optional(),
});
//# sourceMappingURL=subscription-billing.js.map